/**
 * The parent product's own fields that its cluster dropdown option is built from.
 */
export interface ClusterParentDetails {
  season?: string | null | undefined;
  month?: string | number | null | undefined;
  year?: string | number | null | undefined;
  createdAt?: string | undefined;
  image?: string | null | undefined;
  fullImg?: string | null | undefined;
  thumbNailImg?: string | null | undefined;
}

/**
 * A product card's data. Once a cluster member is selected it holds that member's fields,
 * with the parent's own fields kept in `clusterParent`.
 */
export interface ClusterCardProduct extends ClusterParentDetails {
  _id?: string | undefined;
  number?: string | null | undefined;
  clusterParent?: ClusterParentDetails | undefined;
}

/**
 * One option in the cluster dropdown.
 */
export interface ClusterOption {
  _id?: string | undefined;
  number: string;
  createdAt?: string | undefined;
  image?: string | null | undefined;
  fullImg?: string | null | undefined;
  thumbNailImg?: string | null | undefined;
}

/**
 * Builds the cluster dropdown label for a product in the `Code - Season, Month, Year` shape.
 * Each detail is optional and skipped when empty; with no details the label is just the code.
 * @param code the product number
 * @param season the season name
 * @param month the month
 * @param year the year
 * @returns the display label, e.g. `SMVR-001 - Spring, March, 2027`
 */
export const formatClusterOptionLabel = (
  code?: string | null,
  season?: string | null,
  month?: string | number | null,
  year?: string | number | null,
): string => {
  const details = [season, month, year].filter((detail) => detail !== undefined && detail !== null && String(detail).trim() !== '');
  const label = code ?? '';
  return details.length ? `${label} - ${details.join(', ')}` : label;
};

/**
 * Captures the parent's own fields before a cluster member's data replaces the card, carrying
 * an earlier capture forward so a later selection never overwrites it with a member's fields.
 * @param product the card's product right before the selected member replaces it
 * @returns the parent's own season, month, year, created date and images
 */
export const captureClusterParent = (product: ClusterCardProduct): ClusterParentDetails =>
  product.clusterParent ?? {
    season: product.season,
    month: product.month,
    year: product.year,
    createdAt: product.createdAt,
    image: product.image,
    fullImg: product.fullImg,
    thumbNailImg: product.thumbNailImg,
  };

/**
 * Builds the parent's cluster dropdown option from the parent's own fields, never from the
 * cluster member currently shown on the card.
 * @param product the card's product
 * @returns the parent's option for the cluster dropdown
 */
export const buildParentClusterOption = (product: ClusterCardProduct): ClusterOption => {
  const parentDetails = product.clusterParent ?? product;
  return {
    _id: product._id,
    number: formatClusterOptionLabel(product.number, parentDetails.season, parentDetails.month, parentDetails.year),
    createdAt: parentDetails.createdAt,
    image: parentDetails.image,
    fullImg: parentDetails.fullImg,
    thumbNailImg: parentDetails.thumbNailImg,
  };
};
